import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'

import { listAlarmLogEntries } from '../../pages/alarms/api'
import { commonApis } from '../Api/commonApis'

// Shared inbox queries.
//
// Centralises the two queries that power the header bell badge and the
// notification dropdown. Both surfaces read the same queryKey so React
// Query dedupes the request — opening the dropdown doesn't trigger a
// refetch, and the header badge updates the moment the dropdown marks
// something as read.
export function useInboxCounts({ enabled = true, notificationsLimit = 25, alarmsLimit = 25 } = {}) {
  const notificationsQuery = useQuery({
    queryKey: ['notifications', 'inbox', { limit: notificationsLimit }],
    queryFn: () => commonApis.getNotifications({ page: 1, limit: notificationsLimit }),
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  })

  // Fetch only the unconfirmed (un-read) alarms for the dropdown list.
  // The backend's `readFilter=UNCONFIRMED` query param filters
  // server-side, and the same response carries a `meta.confirmed` /
  // `meta.unconfirmed` pair so the bell badge and "X need attention"
  // footer stay accurate without an extra round-trip. Read status is
  // independent of `status` (an alarm can be ACTIVE but already
  // acknowledged) — relying on the meta counters avoids the old
  // local-only "status !== RESOLVED" heuristic which silently
  // double-counted acknowledged-but-still-active rows.
  const alarmsQuery = useQuery({
    queryKey: ['alarm-log', 'inbox', 'unconfirmed', { limit: alarmsLimit }],
    queryFn: () =>
      listAlarmLogEntries({
        page: 1,
        limit: alarmsLimit,
        readFilter: 'UNCONFIRMED',
        orderBy: 'createdAt',
        order: 'DESC',
      }),
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  })

  const counts = useMemo(() => {
    const ntfRaw = notificationsQuery.data?.data?.data ?? notificationsQuery.data?.data ?? []
    const alarmRaw = alarmsQuery.data?.data?.data ?? alarmsQuery.data?.data ?? []
    const ntfList = Array.isArray(ntfRaw) ? ntfRaw : []
    const alarmList = Array.isArray(alarmRaw) ? alarmRaw : []
    // The server returns `meta.unconfirmed` as the app-wide unread
    // count (independent of the paged slice in `data`), so prefer it
    // when present — otherwise fall back to the page length.
    const meta = alarmsQuery.data?.data?.meta || {}
    const serverUnconfirmed = Number(meta.unconfirmed)
    const alarmsUnread =
      Number.isFinite(serverUnconfirmed) && serverUnconfirmed >= 0
        ? serverUnconfirmed
        : alarmList.length
    return {
      notificationsUnread: ntfList.filter((n) => !(n?.isRead ?? n?.read ?? n?.readAt)).length,
      alarmsUnread,
      alarmsConfirmed: Number(meta.confirmed) || 0,
      notificationsTotal: ntfList.length,
      alarmsTotal: alarmList.length,
      notificationsLoading: notificationsQuery.isLoading,
      alarmsLoading: alarmsQuery.isLoading,
    }
  }, [
    notificationsQuery.data,
    alarmsQuery.data,
    notificationsQuery.isLoading,
    alarmsQuery.isLoading,
  ])

  return { counts, notificationsQuery, alarmsQuery }
}

export default useInboxCounts
